import type { Media, profileMedia, Review } from 'store/apiTypes';

export type ReviewListState = {
  data: ReviewListResponse;
  selectedReviewData: ReviewListResponse;
  isSelectedReview: boolean;
  isFetching: boolean;
};

export type ReviewListResponse = {
  offset: number;
  limit: number;
  total: number;
  contents: Review[];
};

export type ReviewContent = {
  id: string;
  title: string;
  comment: string;
  isAnonymous: boolean;
  isLiked: boolean;
  viewCount: number;
  likeCount: number;
  status: string;
  deleteReason: string;
  postDate: string;
  provider: Provider;
  reservation: Reservation;
  overallScores: number;
  scores: Score[];
  questions: Question[];
  author: Author;
  replies: Reply[];
  media: Media[];
};

export type Provider = {
  id: string;
  name: string;
  thumbnailUrl: string;
  detailRegion: string;
};

export type Reservation = {
  providerId: string;
  checkInDate?: string;
  checkOutDate?: string;
  plan: Plan;
  item: Item;
};

export type Plan = {
  id: string;
  name: string;
  publicationStartDate: string;
  publicationEndDate: string;
};

export type Item = {
  id: string;
  name: string;
  publicationStartDate: string;
  publicationEndDate: string;
};

export type Score = {
  category: string;
  code: string;
  orderNo: number;
  score: number;
};

export type Question = {
  category: string;
  code: string;
  orderNo: number;
  value: string;
};

export type Author = {
  userId: string;
  profileAvatar: profileMedia;
  coverImage: profileMedia;
  userBio: string;
  nickname: string;
  gender: Gender;
  likeCount: number;
  viewCount: number;
  reviewCount: number;
  isAgeAndGenderVisible: boolean;
  ageRange: string;
  snsLinks: SnsLink[];
};

export type SnsLink = {
  platform: string;
  url: string;
  orderNo: number;
};

export type Reply = {
  replyId: string;
  reviewId: string;
  authorType: string;
  authorId: string;
  authorName: string;
  postDate: string; // Format: yyyy-MM-dd
  content: string;
  parentId: string;
  active: boolean;
  deleteDate: string; // Format: yyyy-MM-dd
};

export type SortBy =
  | 'RECOMMENDED'
  | 'LATEST'
  | 'HIGHEST_REVIEW_SCORE'
  | 'LOWEST_REVIEW_SCORE'
  | 'MY_REVIEW_PAGE';
export type Purposes = 'LEISURE' | 'BUSINESS';
export type Companions = 'FAMILY' | 'FRIEND' | 'LOVER' | 'COLLEAGUE' | 'SOLO';
export type Gender = 'MALE' | 'FEMALE';
export type MediaType = 'IMAGE' | 'VIDEO';

export type ReviewRequestBody = {
  sortBy: SortBy;
  offset: number;
  limit: number;
  userId?: string;
  reservationId?: string;
  keywords?: string[];
  topics?: string[];
  stayedMonths?: string[];
  overallScores?: string[];
  purposes?: Array<Purposes>;
  companions?: Array<Companions>;
  additionalInformation?: string[];
  ageRanges?: string[];
  genders?: Array<Gender>;
  hasMedia?: boolean;
  providerId?: string;
  requestFrom: RequestFrom;
  reviewId?: string;
};

export const REQUEST_FROM = {
  MY_REVIEW: 'MY_REVIEW',
  PROVIDER_REVIEW: 'PROVIDER_REVIEW',
} as const;
export type RequestFrom = keyof typeof REQUEST_FROM;
